import React from "react";
import { Button } from "@/components/ui/button";
import { DeliveryMap } from "@/components/DeliveryMap";
import { Navigation, Zap, Loader2 } from "lucide-react";
import { vehicleTypes } from "./constants";

export function PriceSummaryPanel({
  estimatedPrice,
  estimatedDistance,
  amountToPay,
  walletDeduction,
  useWallet,
  paymentMethod,
  vehicleType,
  isFormValid,
  searchingPartner,
  onBookNow
}) {
  const selectedVehicle = vehicleTypes.find(v => v.id === vehicleType);

  return (
    <div className="sticky top-24 bg-card border border-border p-6 space-y-6">
      {/* Map Preview */}
      <div className="border border-border overflow-hidden">
        <DeliveryMap height="200px" showRoute={false} />
      </div>

      {/* Price Display */}
      <div className="text-center py-4">
        {estimatedPrice ? (
          <>
            <p className="text-sm text-muted-foreground mb-1">
              {useWallet && walletDeduction > 0 ? "Amount to Pay" : "Estimated Price"}
            </p>
            {useWallet && walletDeduction > 0 ? (
              <>
                <p className="text-lg line-through text-muted-foreground">₹{estimatedPrice}</p>
                <p className="text-4xl font-black text-accent" data-testid="amount-to-pay">
                  {amountToPay === 0 ? "FREE" : `₹${amountToPay}`}
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  ₹{walletDeduction.toFixed(2)} from wallet
                </p>
              </>
            ) : (
              <p className="text-4xl font-black text-accent" data-testid="estimated-price">
                ₹{estimatedPrice}
              </p>
            )}
            {estimatedDistance && (
              <p className="text-sm text-muted-foreground mt-1">
                ~{estimatedDistance} km
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <Navigation className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Enter locations to see price</p>
          </div>
        )}
      </div>

      {/* Price Breakdown */}
      {estimatedPrice && (
        <div className="space-y-2 text-sm border-t border-border pt-4">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Base fare</span>
            <span>₹{selectedVehicle?.basePrice || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Distance fare</span>
            <span>₹{estimatedDistance ? estimatedDistance * (selectedVehicle?.perKmPrice || 0) : 0}</span>
          </div>
          {paymentMethod === 'online' && (
            <div className="flex justify-between text-green-600">
              <span>Online discount</span>
              <span>-5%</span>
            </div>
          )}
          {paymentMethod === 'cod' && (
            <div className="flex justify-between text-amber-600">
              <span>COD charge</span>
              <span>+₹20</span>
            </div>
          )}
          {paymentMethod === 'pay_later' && (
            <div className="flex justify-between text-blue-600">
              <span>Pay later fee</span>
              <span>+₹10</span>
            </div>
          )}
        </div>
      )}

      {/* ETA Info */}
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground border-t border-border pt-4">
        <Zap className="w-4 h-4 text-amber-500" />
        <span>Partner assigned within 2 mins</span>
      </div>

      {/* Book Now Button */}
      <Button 
        onClick={onBookNow}
        disabled={!isFormValid || searchingPartner}
        className="w-full h-14 rounded-none bg-accent text-accent-foreground hover:bg-accent/90 text-lg font-bold"
        data-testid="book-now-btn"
      >
        {searchingPartner ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Finding Partner...
          </span>
        ) : (
          <>Book Now{estimatedPrice ? ` • ₹${estimatedPrice}` : ''}</>
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        {paymentMethod === 'cod' && "Pay cash when your package is delivered"}
        {paymentMethod === 'pay_later' && "Pay within 24 hours after delivery"}
        {paymentMethod === 'online' && "Secure online payment via UPI/Card"}
      </p>
    </div>
  );
}
